import { ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';

interface PageContainerProps {
  children: ReactNode;
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'container' | 'wide' | 'full';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'responsive' | 'mobile-minimal';
}

/**
 * ページコンテナコンポーネント
 * 全ページで統一されたレイアウトコンテナを提供し、レスポンシブ対応と一貫したパディングを実現する
 * 
 * @param children - コンテナ内の子コンポーネント
 * @param className - 追加のCSSクラス名
 * @param maxWidth - 最大幅の設定
 * @param padding - パディングの設定
 * @returns ページコンテナコンポーネント
 */
export const PageContainer: React.FC<PageContainerProps> = ({
  children,
  className,
  maxWidth = 'container',
  padding = 'responsive'
}) => {
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    container: 'max-w-7xl', // 1280px - 最適な可読性を提供
    wide: 'max-w-screen-2xl', // 1536px - より幅広いレイアウト用
    full: 'max-w-full'
  };

  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    responsive: 'px-4 py-4 sm:px-6 sm:py-6 md:px-4 md:py-6 lg:px-8 lg:py-8', // iPadでのパディング最適化
    'mobile-minimal': 'px-2 py-2 sm:px-4 sm:py-4 md:px-6 md:py-6' // モバイル最小余白
  };

  return (
    <div
      className={cn(
        'w-full mx-auto',
        maxWidthClasses[maxWidth],
        paddingClasses[padding],
        className
      )}
    >
      {children}
    </div>
  );
};